import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { gql } from 'apollo-angular';
import { GraphQLError } from 'graphql';
import { Observable, Subject, catchError, firstValueFrom, map, of } from 'rxjs';
import { GQLErrorCodeEnum } from 'src/app/shared/enums/graphql-error-code.enum';
import { WorkerSearchFilterAttribute as WorkerSearchFilterAttributeNew } from 'src/app/shared/graphql/graphql-types';
import { ClientSearchWorkersQueryService } from 'src/app/shared/graphql/worker-search/client-search-workers-query.graphql.autogenerated';
import { SearchWorkersByNameQueryService } from 'src/app/shared/graphql/worker-search/search-workers-by-name-query.graphql.autogenerated';
import { SearchFiltersResponse } from 'src/app/shared/models/gql/search-filters.model';
import { PaginatedQueryPage } from 'src/app/shared/models/gql/special/paginated-query-page.model';
import { WorkerSearchFilterRequest } from 'src/app/shared/models/gql/worker-search-filter.model';
import { WorkerType } from 'src/app/shared/models/worker-type';
import { states } from 'src/app/shared/services.filters';
import { isGraphQLErrorCode } from 'src/app/shared/utilities/graphql-error-handling';
import { User } from '../../shared/models/gql/user.model';
import { WorkerProfile } from '../../shared/models/gql/worker-profile.model';
import { ApiInterfaceService } from '../../shared/services/api-interface.service';

// shortlist worker type
// id - worker id
// isShortlisted - worker is shortlisted or not
type shortlistWorker = { id: string | number; isShortlisted: boolean };

/**
 * Provides interface to search carer profiles.
 */
@Injectable({
  providedIn: 'root',
})
export class ProfileSearchService {
  // filter object used for track worker search filters
  private workerSearchFilters: WorkerSearchFilterRequest = {
    suburb: {
      txt: '',
      idx: null,
    },
    advanced: {
      gender: [],
      reviewChildrenCheckVerified: [],
      myType: [],
      services: [],
      careKnowledge: [],
      availableDays: [],
      properties: [],
      qualifications: [],
      languages: [],
      interests: [],
      culturalBackgrounds: [],
      religions: [],
    },
  };

  // This variable is used to pass private jobs object
  private _shortlistWorkerObject: Subject<shortlistWorker> = new Subject<shortlistWorker>();

  states = states();

  constructor(
    private api: ApiInterfaceService,
    private clientSearchWorkersQueryService: ClientSearchWorkersQueryService,
    private searchWorkersByNameQueryService: SearchWorkersByNameQueryService,
    private router: Router,
  ) {}

  /**
   * Getter to get the worker search filters.
   *
   * @readonly
   * @type {WorkerSearchFilterRequest}
   * @memberof ProfileSearchService
   */
  get workerSearchAppliedFilters(): WorkerSearchFilterRequest {
    return this.workerSearchFilters;
  }

  /**
   * Setter to update worker search filters.
   *
   * @memberof ProfileSearchService
   */
  set workerSearchAppliedFilters(workerSearchFilter: WorkerSearchFilterRequest) {
    Object.keys(workerSearchFilter).forEach((key) => {
      this.workerSearchFilters = {
        ...this.workerSearchFilters,
        [key]: { ...this.workerSearchFilters[key], ...workerSearchFilter[key] },
      };
    });
  }

  /**
   * set shortlist worker object using behavior subject.
   *
   * @param shortlistWorkerData -  - shortlist worker object data.
   */
  set shortlistWorkerSource$(shortlistWorkerData: shortlistWorker) {
    this._shortlistWorkerObject.next(shortlistWorkerData);
  }

  /**
   * Getter for shortlist worker object.
   *
   */
  get shortlistWorker$(): Observable<shortlistWorker> {
    return this._shortlistWorkerObject.asObservable();
  }

  /**
   * GET a carer's profile name for the provided id.
   *
   * @param carerProfileId - The id OR permalink of the carer to retrieve.
   */
  async getCarerNameByIdOrPermalink(carerProfileId): Promise<User> {
    try {
      const res = await this.api.gqlQuery(
        gql`
          query ($id: ID) {
            workerProfile(userId: $id) {
              user {
                firstName
              }
            }
          }
        `,
        {
          id: carerProfileId,
        },
      );
      return res['workerProfile']['user'];
    } catch (e) {
      return Promise.reject(e);
    }
  }

  /**
   * GET a carer's profile for the provided id.
   *
   * @param carerProfileId - The id of the carer to retrieve.
   * @param mechanism - To track how the client and worker used to find each other example "individual_message" | "rec_worker" | "job_search" | "job_rec" | "byo" | "bulk_message" | "rec_worker_job_page".
   * @param searchToken - Token for last worker searched on search worker page.
   * @param paramLink - The paramLink of the carer to retrieve.
   */
  async getCarerProfile(
    carerProfileId: number | string,
    mechanism: string,
    searchToken: string,
    paramLink: string,
  ): Promise<WorkerProfile> {
    try {
      const res = await this.api.gqlQuery(
        gql`
          query ($id: ID, $mechanism: String, $searchToken: String) {
            workerProfile(userId: $id, mechanism: $mechanism, searchToken: $searchToken) {
              memberSince
              about
              adminBadges
              immunisation {
                title
                verified
                updatedAt
              }
              workHistory {
                company
                endAt
                serviceProvided
                startAt
                title
              }
              availabilityUpliftFeedbackBannerVisible
              availabilityUpdatedAt
              availability {
                available
                friday
                monday
                saturday
                sunday
                thursday
                tuesday
                wednesday
                mondayTimes {
                  id
                  startTime
                  endTime
                }
                tuesdayTimes {
                  id
                  startTime
                  endTime
                }
                wednesdayTimes {
                  id
                  startTime
                  endTime
                }
                thursdayTimes {
                  id
                  startTime
                  endTime
                }
                fridayTimes {
                  id
                  startTime
                  endTime
                }
                saturdayTimes {
                  id
                  startTime
                  endTime
                }
                sundayTimes {
                  id
                  startTime
                  endTime
                }
                isMigratedToAvailabilityUplift
              }
              badges {
                description
                icon
                name
                showIcon
                value
              }
              educationAndTraining {
                degree
                endAt
                institution
                startAt
              }
              experiences {
                agedCare {
                  name
                }
                chronicMedicalCondition {
                  name
                }
                disability {
                  name
                }
                general {
                  name
                }
                mentalHealth {
                  name
                }
              }
              firstHourForFree
              hasCovidVaccine
              hasSeasonalFluShot
              id
              interests {
                name
              }
              languages {
                firstLanguages {
                  name
                }
                secondLanguages {
                  name
                }
              }
              loyaltyName
              culturalBackgrounds {
                asian {
                  name
                }
                general {
                  name
                }
                middleEast {
                  name
                }
                southEastEurope {
                  name
                }
                westEurope {
                  name
                }
              }
              religion {
                name
              }
              lgbtiFriendly
              locations {
                longitude
                latitude
                name
              }
              nonSmoker
              normalizedType
              offeredServices {
                description
                name
                selected
                sortValue
              }
              personalities {
                sortValue
                name
                selected
              }
              petFriendly
              response {
                rate
                time
                veryResponsive
                timePersonal
              }
              clientFields {
                hasLmj
                hasReviewableAgreement
                shortlisted
                showStatDecInfoPanel
              }
              bypassEmptyCheck
              ratings {
                anonymous
                createdAt
                description
                value
                sender {
                  avatar {
                    small
                    big
                    medium
                  }
                  firstName
                  fullName
                }
              }
              rates {
                clientNetDollars
                flatRate
                rateName
                workerNetDollars
                dollars
                grossDollars
              }
              sectionVisibility {
                showMemberSinceLabel
                showDeliveredHoursCombinedLabel
                careExperienceFlags {
                  askForMoreExperience
                  displaySection
                  enabled
                  fillMainExperience
                  fillProfessionalExperience
                }
              }
              careExperienceAndKnowledge {
                summary
                mentalHealth {
                  kind
                  selected
                  description
                  experience {
                    id
                    name
                    selected
                    sortValue
                  }
                  knowledge {
                    id
                    name
                    selected
                    sortValue
                  }
                }
                disability {
                  kind
                  selected
                  description
                  experience {
                    id
                    name
                    selected
                    sortValue
                  }
                  knowledge {
                    id
                    name
                    selected
                    sortValue
                  }
                }
                chronicMedicalCondition {
                  kind
                  selected
                  description
                  experience {
                    id
                    name
                    selected
                    sortValue
                  }
                  knowledge {
                    id
                    name
                    selected
                    sortValue
                  }
                }
                agedCare {
                  kind
                  selected
                  description
                  experience {
                    id
                    name
                    selected
                    sortValue
                  }
                  knowledge {
                    id
                    name
                    selected
                    sortValue
                  }
                }
              }
              verified {
                badges {
                  details
                  icon
                  title
                }
                qualifications {
                  details
                  title
                }
                ticks {
                  details
                  icon
                  title
                }
              }
              combinedFeedback
              deliveredHourGroup
              myType
              user {
                id
                uuid
                avatar {
                  small
                  big
                  medium
                }
                userHasBlocked
                userIsBlocked
                displayName
                firstName
                fullName
                lastName
                truncatedName
              }
              shareableReferencesSummary {
                heading
                data
                visible
                pending
              }
              currentWorkingWithChildrenCheck
              workingWithChildren
            }
          }
        `,
        {
          id: carerProfileId,
          mechanism,
          searchToken,
          permalink: paramLink,
        },
      );
      return res['workerProfile'];
    } catch (e) {
      return Promise.reject(e);
    }
  }

  /**
   * Get list of services base on worker_type/care type
   *
   * @param type
   */
  async getServicesList(type): Promise<[]> {
    const res = await (<any>(await this.api.get(`/api/v1/services?filter[category]=${type}`, true)).toPromise());
    return res.data.map((service) => {
      service.attributes.id = service.attributes.sort_value;
      service.attributes.isImportant = service.attributes.important;
      return service.attributes;
    });
  }

  /**
   * Catches errors related to the client being soft banned and implementing actions based on the error code.
   *
   * @param errorObject - Error object typically coming from the catch statement.
   */
  private softBanErrorHandler(errorObject: GraphQLError[] | { graphQLErrors: GraphQLError[] }): void {
    if (isGraphQLErrorCode(errorObject, GQLErrorCodeEnum.ACTOR_SUSPENDED)) {
      // If user is soft-banned we need to redirect him/her back to the dashboard where
      // account restriction notice will be presented to them.
      this.router.navigate(['/']);
    }
  }

  /**
   * get rates and myType from carer's profile
   *
   * @param includeRateSchedule - Feature flag for getting rateSchedule. Defaults to FALSE.
   * @returns - worker profile containing indicative rates.
   */
  async getWorkerProfileProperties(includeRateSchedule = false): Promise<WorkerProfile> {
    try {
      const res = await this.api.gqlQuery(gql`
        {
          workerProfile {
            id
            myType
            rates {
              clientNetDollars
              flatRate
              rateName
              workerNetDollars
              dollars
              grossDollars
              ${
                includeRateSchedule
                  ? `
                rateDescription
                rateSchedule {
                  weekday
                  saturday
                  sunday
                  publicHoliday
                  daytime
                  evening
                  overnight
                }
              `
                  : ''
              }
            }
            user {
              id
              uuid
            }
          }
        }
      `);
      return res['workerProfile'];
    } catch (e) {
      return Promise.reject(e);
    }
  }

  /**
   * Gets worker type(s) given the support provider id.
   *
   * @param supportProviderId - support provider id.
   * @returns worker type(s).
   */
  async getWorkerType(supportProviderId: number | string): Promise<WorkerType> {
    try {
      const res = await this.api.gqlQuery(
        gql`
          query ($supportProviderId: ID) {
            workerProfile(userId: $supportProviderId) {
              myType
            }
          }
        `,
        {
          supportProviderId,
        },
      );
      return res['workerProfile']['myType'];
    } catch (error) {
      return Promise.reject(error);
    }
  }

  /**
   * Gets workerProfile with sectionVisibility information.
   */
  async getWorkerProfileSectionVisibility(): Promise<WorkerProfile> {
    try {
      const res = await this.api.gqlQuery(gql`
        {
          workerProfile {
            sectionVisibility {
              applyToJob
              showSubmitAbnCta
            }
          }
        }
      `);
      return res['workerProfile'];
    } catch (e) {
      Promise.reject(e);
    }
  }

  /**
   * Get worker result based on the selected filters.
   *
   * @param after - After use to get the next page data for the specified cursor value.
   */
  async searchWorkersByLocation(after?: string): Promise<PaginatedQueryPage<WorkerProfile>> {
    try {
      const res = await firstValueFrom(
        this.clientSearchWorkersQueryService.fetch({
          first: 20,
          after,
          // TODO(ES-43797): Remove typecasting once we can rework the state management for the worker search and the old GQL typings
          filters: this.workerSearchFilters as unknown as WorkerSearchFilterAttributeNew,
        }),
      );

      // TODO(ES-43797): Remove typecasting here as well.
      return res.data.clientSearchWorkers as unknown as PaginatedQueryPage<WorkerProfile>;
    } catch (error) {
      this.softBanErrorHandler(error);
      return Promise.reject(error);
    }
  }

  /**
   * Get worker result based on their name.
   *
   * @param name - Name of the worker to search for.
   * @param after - The endCursor of page info returned from recent query.
   */
  async searchWorkersByName(name: string, after?: string): Promise<PaginatedQueryPage<WorkerProfile>> {
    try {
      const res = await firstValueFrom(
        this.searchWorkersByNameQueryService.fetch({
          name,
          first: 20,
          after,
        }),
      );

      // TODO(ES-43797): Remove typecasting once we can rework the state management for the worker search and the old GQL typings
      return res.data.searchWorkersByName as unknown as PaginatedQueryPage<WorkerProfile>;
    } catch (error) {
      this.softBanErrorHandler(error);
      return Promise.reject(error);
    }
  }

  /**
   * Get all available search filters when clients are searching for workers.
   */
  async getSearchFilters(): Promise<SearchFiltersResponse[]> {
    try {
      const searchFilterData: any = await this.api.gqlQuery(gql`
        query {
          clientSearchWorkerFilters {
            advanced
            code
            description
            name
            showAsDropdown
            filters {
              dialog
              id
              important
              name
              order
              showByDefault
              sortValue
            }
            subcategories {
              advanced
              code
              description
              name
              showAsDropdown
              filters {
                dialog
                id
                important
                name
                order
                showByDefault
                sortValue
              }
            }
          }
        }
      `);
      return searchFilterData['clientSearchWorkerFilters'];
    } catch (e) {
      return Promise.reject(e);
    }
  }

  /**
   * Get search worker match count on the selected filters.
   *
   */
  async getWorkersMatchCount(): Promise<number> {
    try {
      const workerMatchResponse = await this.api.gqlQuery(
        gql`
          query searchWorkers($filters: WorkerSearchFilterAttribute) {
            clientSearchWorkers(filters: $filters) {
              totalCount
            }
          }
        `,
        {
          filters: this.workerSearchFilters,
        },
      );
      return workerMatchResponse['clientSearchWorkers']['totalCount'];
    } catch (e) {
      return Promise.reject(e);
    }
  }

  /**
   * Get default suburb from user entity.
   *
   * @returns - return suburb and geo_id
   */
  getDefaultSuburb(): Observable<Partial<{ txt: string; idx: number }>> {
    return this.api
      .gqlQuery$<{ user: User }>(gql`
        query {
          user {
            addressLine2
            state
            postcode
            postcodeGeoId
          }
        }
      `)
      .pipe(
        map((result) => {
          // Making sure that all the required properties are present in query result.
          if (
            !!result.user.addressLine2 &&
            !!result.user.state &&
            !!result.user.postcode &&
            !!this.states[result.user.state] &&
            !!result.user.postcodeGeoId
          ) {
            return {
              txt: `${result.user.addressLine2} ${this.states[result.user.state]} ${result.user.postcode}`,
              idx: result.user.postcodeGeoId,
            };
          }
          return {};
        }),
        catchError(() => of({})),
      );
  }

  /**
   * Set default suburb value.
   */
  async setDefaultSuburb(): Promise<void> {
    // get user details
    const defaultSuburb = await firstValueFrom(this.getDefaultSuburb());
    this.workerSearchAppliedFilters = {
      suburb: {
        txt: defaultSuburb.txt,
        idx: defaultSuburb.idx,
      },
    };
  }

  /**
   * Retrieves the information of approvedCarerWithoutTimesheet flag.
   */

  getApprovedCarerWithoutFirstTimesheet$(): Observable<boolean> {
    type Query = { user: User };
    return this.api
      .gqlQuery$<Query>(gql`
        {
          user {
            approvedCarerWithoutTimesheet
          }
        }
      `)
      .pipe(map((result) => result?.user?.approvedCarerWithoutTimesheet));
  }
}
